import React, { createContext, useContext, useRef, useState } from "react";
import { useImmerReducer } from "use-immer";
import fileFolderConfig from "../commonFunctions/fileFolderConfig";
import axiosInstance, { handleApiError } from "../services/axiosService";
import { processLayout } from "../utils/GridLayoutFunc";
import { AppBlockReducer } from "./AppBlocksContext/AppBlockReducer";
import { useTreeContext } from "./TreeDataContext";
// import { Navigate } from "react-router-dom";
import { convertPixelsToGrid, getElementIds } from "../Pages/FlexibleLayout/utils";
import { processChartLayout } from "../utils/InitialRenderingData";
import { getTreeDataForApplicationBlock } from "../utils/treeData/treeDataUtils";
import { insertNewFileOrFolder } from "../utils/treeData/folderEditorUtils";
import { updateApplicationBlockChecksum } from "../utils/configChecksum/setterFunctions/appblockConfig";

const AppBlocksContextApi = createContext();

const useAppBlocksContext = () => useContext(AppBlocksContextApi);
const AppBlocksContext = ({ children }) => {
  const [selectedAppBlock, setSelectedAppBlock] = useState({
    id: "",
    name: "",
    parentData: "",
    table_id: "",
    activity_id: ""
  });
  const { blocksExplorerData, setBlocksExplorerData } = useTreeContext();

  const [allBlocksData, setAllBlocksData] = useState([]);
  const [allApplicationBlockData, setAllApplicationBlockData] = useState({});
  //handle data with immer
  const getBlockAndDeviceType = () => {
    try {
      const [deviceType, blockType] = selectedAppBlock.parentData.split("/");
      return { blockType: blockType || "", deviceType: deviceType || "" };
    } catch (error) {
      return { blockType: "", deviceType: "" };
    }
  };
  const { blockType, deviceType } = getBlockAndDeviceType();
  const [blockData, blockDataDispatch] = useImmerReducer(AppBlockReducer(blockType, deviceType), { config: null });

  // store current block data
  const [applicationBlockData, setApplicationBlockData] = useState({});

  // lock
  const [lockData, setLockData] = useState({});

  // recordview desktop
  const [recordsviewLayout, setRecordsviewLayout] = useState(null);

  //detailview
  const [layout, setLayout] = useState([]);
  const layoutData = useRef({});
  const nestedGridData = useRef({});

  // charts
  const initialChartConfig = {
    global_filters: {
      fields: [],
      pre_filters: [],
      owner_pre_filters: {
        logged_user: false,
        next_activities: [],
        hide_activities: false
      }
    },
    tiles: [],
    styles: {}
  };
  const [chartGlobalFilter, setChartGlobalFilter] = useState(initialChartConfig);
  const [layoutConfig, setLayoutConfig] = useState([]);

  // kanban and calendar
  const [currentBlockConfig, setCurrentBlockConfig] = useState(null);

  const fetchAllApplicationBlock = async ({ signal } = {}) => {
    const endpoints = {
      recordsview: "/recordsview",
      detailsview: "/detailsview",
      chart: "/chart",
      activitycard: "/activitycard",
      progressbar: "/progress-bar",
      pivotcharts: "/pivotcharts",
      queryblocks: "/queryblocks"
    };

    // ---- 1) Fire all requests in parallel (with abort support) ----
    const responses = await Promise.all(
      Object.entries(endpoints).map(([key, url]) =>
        axiosInstance.get(url, { signal }).then((res) => ({
          key,
          data: res.data
        }))
      )
    );

    // ---- 2) Build tree input in consistent sorted order ----
    const orderedKeys = [
      "recordsview",
      "detailsview",
      "chart",
      "pivotcharts",
      "queryblocks",
      "activitycard",
      "progressbar"
    ];

    const orderedDataArray = orderedKeys.map((k) => responses.find((r) => r.key === k)?.data);

    const treeData = getTreeDataForApplicationBlock(orderedDataArray);

    setBlocksExplorerData(treeData);

    // ---- 3) Build final map: { id: item, id2: item2, ... } ----
    const finalObject = responses.reduce((result, { data }) => {
      Object.values(data || {}).forEach((arr) => {
        if (Array.isArray(arr)) {
          arr.forEach((item) => {
            if (item?.id) result[item.id] = item;
          });
        }
      });
      return result;
    }, {});

    setAllApplicationBlockData(finalObject);

    return { data: responses, treeData };
  };

  const getDetailviewConfig = (convertFieldToEmpty = false) => {
    let { elements, uniqueFields, uniqueIds } = processLayout(layoutData, nestedGridData, layout, convertFieldToEmpty);
    const { data, device, position_block_metadata = {}, position_block_data_ids = {} } = applicationBlockData;
    const configData = {
      ...data,
      elements: elements,
      data_fields: uniqueFields,
      unique_ids: uniqueIds
    };

    return {
      ...applicationBlockData,
      data: {
        ...configData,
        config: {
          ...configData?.config,
          position_config_for_non_activity: data?.config?.position_config_for_non_activity,
          position_config_ids: data?.config?.position_config_for_non_activity
            ? getElementIds(data?.config?.position_config_for_non_activity)
            : data?.config?.position_config_ids
        }
      },
      position_block_config: (() => {
        if (device === "mobile") {
          return layoutConfig ? convertPixelsToGrid(layoutConfig, position_block_metadata?.grid_size) : null;
        } else {
          return layoutConfig;
        }
      })(),
      position_block_data_ids: layoutConfig ? getElementIds(layoutConfig) : position_block_data_ids
    };
  };

  const getChartBlockConfig = () => {
    const chartsArray = processChartLayout(layout, layoutData.current);

    const newConfig = {
      ...applicationBlockData.config,
      charts: chartsArray,
      ...chartGlobalFilter
    };
    if (applicationBlockData?.is_positioned_block) {
      return {
        ...applicationBlockData,
        config: newConfig,
        position_block_data_ids: layoutConfig
          ? getElementIds(layoutConfig)
          : applicationBlockData.position_block_data_ids,
        position_block_config: (() => {
          if (applicationBlockData.device === "mobile") {
            return layoutConfig
              ? convertPixelsToGrid(layoutConfig, applicationBlockData.position_block_metadata?.grid_size)
              : null;
          } else {
            return layoutConfig;
          }
        })()
      };
    } else {
      return { ...applicationBlockData, config: newConfig };
    }
  };

  const getRecordViewConfig = () => {
    const { device } = applicationBlockData;
    if (device === "desktop" || device === "tablet") {
      const copyBlockData = JSON.parse(JSON.stringify(blockData));
      let { elements } = processLayout(layoutData, nestedGridData, layout);
      if (!copyBlockData?.config?.layout?.catalog) {
        copyBlockData.config.layout.catalog = {
          elements: elements
        };
      } else {
        copyBlockData.config.layout.catalog.elements = elements;
      }
      if (copyBlockData.is_positioned_block) {
        copyBlockData.position_block_data_ids = getElementIds(copyBlockData.position_block_config || []);
      }
      return copyBlockData;
    }
    if (device === "mobile") {
      const copyRecordviewLayout = JSON.parse(JSON.stringify(recordsviewLayout));
      let { elements } = processLayout(layoutData, nestedGridData, layout);
      if (!copyRecordviewLayout.catalog) {
        copyRecordviewLayout.catalog = {
          elements: elements
        };
      } else {
        copyRecordviewLayout.catalog.elements = elements;
      }
      let positionConfig = {
        position_block_config: [],
        position_block_data_ids: {}
      };
      if (applicationBlockData.is_positioned_block) {
        positionConfig = {
          position_block_config:
            convertPixelsToGrid(layoutConfig, applicationBlockData.position_block_metadata?.grid_size) || [],
          position_block_data_ids: getElementIds(layoutConfig || []) || {}
        };
      }
      return {
        ...applicationBlockData,
        config: { layout: copyRecordviewLayout },
        ...positionConfig
      };
    }
  };

  const getProgressBarConfig = () => {
    return { ...applicationBlockData, config: currentBlockConfig };
  };
  const getQueryBlockConfig = () => {
    return { ...applicationBlockData, config: currentBlockConfig };
  };
  const getPivotchartConfig = () => {
    const newLayout = JSON.parse(JSON.stringify(recordsviewLayout));
    const { elements } = processLayout(layoutData, undefined, layout);
    newLayout.pivot_charts = elements;

    return {
      ...applicationBlockData,
      config: { ...applicationBlockData.config, layout: newLayout }
    };
  };
  const getAppBlockConfig = (individualBlock) => {
    let type = selectedAppBlock.parentData.split("/")[1];
    if (individualBlock) {
      type = individualBlock;
    }

    switch (type) {
      case "chart":
        return getChartBlockConfig();
      case "detailsview":
      case "activitycard":
        return getDetailviewConfig();
      case "queryblocks":
        return getQueryBlockConfig();
      case "progress-bar":
        return getProgressBarConfig();
      case "pivotcharts":
        return getPivotchartConfig();
      case "recordsview":
        return getRecordViewConfig();
      default:
        break;
    }
  };

  const handleSaveApplicationBlock = async () => {
    const config = getAppBlockConfig();
    const { name: _, ...blockConfig } = config;
    const block = selectedAppBlock.parentData.split("/")[1];
    try {
      const { message } = await axiosInstance.put(`/${block}/${selectedAppBlock.id}`, blockConfig);
      // const newChecksum = createChecksum(config);
      // currentChecksum.current = newChecksum;
      // setInitialAndModifiedChecksum(config);
      updateApplicationBlockChecksum(block, blockConfig);
      return { data: blockConfig, message };
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleCloneApplicationBlock = async () => {
    try {
      await handleSaveApplicationBlock();
      const blockName = selectedAppBlock.parentData.split("/")[1];
      const deviceName = selectedAppBlock.parentData.split("/")[0];
      const { data } = await axiosInstance.post(`${blockName}/${selectedAppBlock.id}/clone`);
      const newItem = fileFolderConfig.getFileConfig(
        data.id,
        data.name,
        `${data.device}/${blockName}`,
        true,
        true,
        { activity_id: null, info_tag: [], is_offline: false },
        data.table_type
      );
      const newTree = insertNewFileOrFolder(blocksExplorerData, { id: `${deviceName}/${blockName}` }, newItem);
      setBlocksExplorerData([...newTree]);
      return data.id;
    } catch (error) {
      handleApiError(error);
    }
  };

  const payloadStateList = [
    applicationBlockData,
    blockData,
    recordsviewLayout,
    layout,
    layoutData.current,
    nestedGridData.current,
    layoutConfig,
    currentBlockConfig,
    chartGlobalFilter
  ];

  return (
    <AppBlocksContextApi.Provider
      value={{
        selectedAppBlock,
        setSelectedAppBlock,
        recordsviewLayout,
        setRecordsviewLayout,
        layout,
        setLayout,
        layoutData,
        nestedGridData,
        chartGlobalFilter,
        setChartGlobalFilter,
        currentBlockConfig,
        setCurrentBlockConfig,
        applicationBlockData,
        setApplicationBlockData,
        lockData,
        setLockData,
        allBlocksData,
        setAllBlocksData,
        allApplicationBlockData,
        setAllApplicationBlockData,
        getAppBlockConfig,
        fetchAllApplicationBlock,
        handleSaveApplicationBlock,
        getDetailviewConfig,
        blockData,
        blockDataDispatch,
        handleCloneApplicationBlock,
        layoutConfig,
        setLayoutConfig,
        payloadStateList
      }}
    >
      {children}
    </AppBlocksContextApi.Provider>
  );
};

export default AppBlocksContext;
export { useAppBlocksContext };
